import { inject, Injectable, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';

import { Preferences } from '@capacitor/preferences';
import { ILog, UbieSipConfiguration } from '@ubie/sip-configuration';
import { CommunicationSendService } from '../services/communication/communication-send/communication-send.service';
import { Functionblock } from '../services/functionblocks/functionblocks.interfaces';
import { LogService } from '../services/log/log.service';
import { SipDevicesService } from '../services/sipDevices/sip-devices.service';

@Injectable({
  providedIn: 'root',
})
export class SipHelper {
  private sipStarted = false;

  private readonly sipDeviceService = inject(SipDevicesService);
  private readonly communicationSendService = inject(CommunicationSendService);
  private readonly logger = inject(LogService);
  private readonly ngZone = inject(NgZone);

  private isDevelop = false;

  setIsDevelop(isDevelop: boolean): void {
    this.isDevelop = isDevelop;
  }

  async get(key: string): Promise<any> {
    if (Capacitor.isNativePlatform()) {
      const item = await Preferences.get({ key: key });
      return JSON.parse(item.value || 'null');
    }

    return 'null';
  }
  /////////////////////////////////////////////////////////
  //#region SIP PROPERTIES
  /////////////////////////////////////////////////////////

  currentDeviceUid = '';
  storageKey = 'sipDeviceUid';
  incomingCall = false;
  private token: any;

  //#endregion
  /////////////////////////////////////////////////////////

  //#region SIP

  async initSip(): Promise<void> {
    if (!Capacitor.isPluginAvailable('UbieSipConfiguration')) {
      return;
    }

    this.logger.info('SIP-HELPER - init sip');

    // get the current device uid
    this.currentDeviceUid = await this.get(this.storageKey);

    this.logger.info('SIP-HELPER - current device uid', this.currentDeviceUid);

    // if the device has a sip device uid, we need to initialize SIP in the java part of the application
    if (
      this.currentDeviceUid !== null &&
      this.currentDeviceUid !== undefined &&
      this.currentDeviceUid !== 'popupClosed'
    ) {
      this.logger.info('SIP-HELPER - init sip getSipDevice');
      this.getSipDevice();
    } else {
      if (this.sipStarted) {
        // if sip was running (check in java), stop sip
        this.logger.info('SIP-HELPER - init sip stopSipOnDevice');
        this.stopSipOnDevice();
      }
    }
    await UbieSipConfiguration.requestPermissions();
  }

  async setPushNotificationToken(value: any): Promise<void> {
    this.logger.info('SIP-HELPER - SipHelper, set token ', value);
    this.token = value;
  }

  /**
   * Stop SIP on device
   */
  stopSipOnDevice(): void {
    if (!Capacitor.isPluginAvailable('UbieSipConfiguration')) {
      return;
    }

    UbieSipConfiguration.stopSip().then(() => {
      this.sipStarted = false;
      this.logger.error('SIP-HELPER - stopped sip');
    });
  }

  /**
   * Check if the call is still active
   * When a call rings, we set a boolean = true, when a call is released, we set the boolean = false
   */
  async isCallActive(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!Capacitor.isPluginAvailable('UbieSipConfiguration')) {
        reject();
      }

      if (this.incomingCall) {
        resolve(this.incomingCall);
        this.incomingCall = false;
      } else {
        // timeout is needed for iOS, when the app is inactive:
        // it takes some time for linphone to start up and process the incoming call
        setTimeout(() => {
          resolve(this.incomingCall);
          this.incomingCall = false;
        }, 2000);
      }
    });
  }

  /**
   * Get sip device
   */
  private getSipDevice(): void {
    // fetch data
    this.sipDeviceService.getSipDeviceConfig(this.currentDeviceUid).subscribe({
      next: (data) => {
        this.logger.info('SIP-HELPER - getSipDevice data', data);

        if (
          data.fullExtension !== null &&
          data.fullExtension !== undefined &&
          data.password !== null &&
          data.password !== undefined &&
          data.domain !== null &&
          data.domain !== undefined
        ) {
          this.startSipOnDevice(data);
        } else {
          // the device is still in the database but is not linked to a doorbell
          this.ngZone.run(() => this.stopSipOnDevice());
        }
      },
      error: (error) => {
        this.logger.error('SIP-HELPER - getSipDevice error', error);
      },
    });
  }

  /**
   * Start SIP on device
   */
  private startSipOnDevice(data: any): void {
    this.logger.debug('start sip on device ' + this.sipStarted);

    // start sip
    if (!this.sipStarted) {
      UbieSipConfiguration.startSip({
        isDevelop: this.isDevelop,
        domain: data.domain,
        extension: data.user.id.toString() + data.extension.toString(),
        password: data.password,
        token: this.token,
      }).then(() => this.logger.info('start sip on device sent'));
    }

    // do not add listeners if sip was already started
    if (!this.sipStarted) {
      // listen for functionblock clicked event
      UbieSipConfiguration.addListener(
        'functionblockClicked',
        (result: any) => {
          this.handleFunctionblockClicked(result);
        },
      );

      // listen for log event
      UbieSipConfiguration.addListener('onLog', (result: ILog) => {
        this.logger.warn('SIP NATIVE - ' + result.message);
      });

      // listen for incoming call event
      UbieSipConfiguration.addListener(
        'incomingCallReceived',
        (result: any) => {
          this.logger.info('SIP-HELPER - Incoming call received ', result);
          this.handleIncomingCall(result);
        },
      );

      // listen for incoming call event
      UbieSipConfiguration.addListener(
        'incomingCallReleased',
        (result: any) => {
          this.logger.info('SIP-HELPER - (incoming) call released ', result);
          this.incomingCall = false;
        },
      );
    }

    this.sipStarted = true;
  }

  private setCallerDetails(name: string, fb: Functionblock): void {
    UbieSipConfiguration.setCallerDetails({
      name: name,
      functionblock: fb,
    }).then(() => this.logger.info('SIP-HELPER - set called details finished'));
  }

  private handleIncomingCall(data: any): void {
    this.logger.info('SIP-HELPER - Handle incoming call', data);
    if (data) {
      if (data.hasOwnProperty('call')) {
        this.incomingCall = true;
        // get caller details from database

        this.getSipDeviceByExtension(data.call);
      }
    }
  }

  /**
   * Get sip device
   */
  private getSipDeviceByExtension(extension: string): void {
    // make sure were logged in

    // fetch data
    this.sipDeviceService.getSipDeviceByExtension(extension).subscribe({
      next: (data) => {
        const name = data.hasOwnProperty('name') ? data.name : '';
        const fb = data.hasOwnProperty('functionblock')
          ? data.functionblock
          : null;
        fb && this.setCallerDetails(name ?? '', fb);
      },
      error: (error) => {
        this.logger.error(
          'SIP-HELPER - error in get sip by device extension',
          error,
        );
      },
    });
  }

  /**
   * Functionblock was clicked in the call screen
   * @param data
   */
  private handleFunctionblockClicked(data: any): void {
    if (data) {
      if (
        data.hasOwnProperty('deviceSerialNumber') &&
        data.hasOwnProperty('networkDeviceSerialNumber') &&
        data.hasOwnProperty('functionblockSerialNumber') &&
        data.hasOwnProperty('functionblockType')
      ) {
        // if the output type is ONOFF --> perform ON action
        if (data.functionblockType.toString().toLowerCase() === 'onoff') {
          this.communicationSendService.setFunctionBlockAction(
            'on',
            data.deviceSerialNumber, // device sn
            data.networkDeviceSerialNumber, // networkdevice sn,
            data.functionblockSerialNumber, // functionblock sn
          );
        } else {
          // if the output type is SCENE --> perform ACTIVE action
          if (data.functionblockType.toString().toLowerCase() === 'scene') {
            this.communicationSendService.setFunctionBlockAction(
              'active',
              data.deviceSerialNumber, // device sn
              data.networkDeviceSerialNumber, // networkdevice sn,
              data.functionblockSerialNumber, // functionblock sn
            );
          }
        }
      }
    }
  }

  //#endregion
}
